import type {
  CreateDashboardAutomatedExportRequest,
  CustomMetricBrandAndOrgRequest,
  CustomMetricCreateRequest,
  CustomMetricPostRequest,
  CustomMetricUpdateRequest,
  DashboardNewOwnershipRequest,
  DashboardRequest,
  DashboardUserRequest,
  GetDashboardsDashboardIdReportsReportIdParams,
  GetDashboardsParams,
  GetMetricDetailsParams,
  GetShareableLinksParams,
  GetShareableLinksUsage200,
  PostCustomMetricsParams,
  PostDashboardsParams,
  ReportCreateRequest,
  ReportUpdateRequest,
  ShareableDashboardLink,
  ShareableDashboardLinkCreateRequest,
  ShareableDashboardLinkListResponse,
  ShareableDashboardLinkUpdateRequest,
  UpdateDashboardAutomatedExportRequest,
} from '@dashhudson/api-client/dashboard/model';
import type {
  BenchmarkIndustry,
  ComparableMetricsResponse,
  CustomMetricResponse,
  Dashboard,
  DashboardAutomatedExport,
  DashboardsResponse,
  DashboardUser,
  DashboardUserListItem,
  Industry,
  IndustryBySourceAccountResponse,
  MetricDetailsResponse,
  MetricTypesResponse,
  PagedCustomMetrics,
  PostCustomMetricsUsage200,
  PutReportsDataParams,
  ReportDataRequestBody,
  ReportDataResponse,
  ReportResponse,
  UserDashboardsAutomatedExports,
} from '@dashhudson/api-client/dashboard/model/raw';

import merge from 'lodash/merge';

import type { HttpRequestConfig } from '@/types/http';

import { createDashHttpClient } from '@/apis/dash.http';
import { env } from '@/env';

export const http = createDashHttpClient(env.dashboardApiUrl);

interface CancelKey {
  brandIds?: number[] | number | string;
  competitorSourceAccountIds?: string[] | string;
  channels?: string[] | string;
  metrics?: string[] | string;
  reportType?: string;
  aggregateBy?: string;
  sourceAdAccountIds?: string[] | string;
  sourceCampaignIds?: string[] | string;
  campaignIds?: number[] | number | string;
  authorship?: readonly string[];
  mediaTypes?: readonly string[] | string;
}

const abortControllers = new Map<string, AbortController>();

const AD_HOC_REPORT_QUERY_KEYS = [
  'brand_ids',
  'brand_tags',
  'channels',
  'metrics',
  'start_date',
  'end_date',
  'context_start_date',
  'context_end_date',
  'sort_order',
  'time_scale',
  'csv_layout',
  'filename_prefix',
  'filters',
  'limit',
  'offset',
  'use_benchmark_cache',
  'source_account_ids',
  'competitor_tags',
  'creator_ids',
  'creator_tag_ids',
  'require_posts',
  'include_share_of_voice',
  'aggregate',
  'aggregate_by',
  'secondary_aggregate_by',
  'content_tag_ids',
  'content_tag_match_type',
  'source_campaign_ids',
  'source_ad_account_ids',
  'campaign_ids',
  'board_ids',
  'media_types',
] as const satisfies readonly (keyof PutReportsDataParams)[];

const AD_HOC_REPORT_BODY_KEYS = [
  'authorship',
  'benchmarks',
  'collaborator_usernames',
  'community',
  'competitor_source_account_ids',
  'display_strategy',
  'goals',
  'listening',
  'source_ad_campaigns',
] as const satisfies readonly (keyof ReportDataRequestBody)[];

// Local response type for the batch endpoint until the OpenAPI spec ships it.
interface ReportsBatchResponse {
  reports: { id: number; client_ref: string; type: string; meta: Record<string, unknown> }[];
  errors: { client_ref: string; index: number; error: string }[];
  dashboard: { id: number; layout: Record<string, unknown>[] };
}

function getAndSetSignal(cancelData: CancelKey): AbortSignal {
  const key = JSON.stringify(cancelData);
  abortControllers.get(key)?.abort();
  const controller = new AbortController();
  abortControllers.set(key, controller);
  return controller.signal;
}

const api = {
  // Dashboards
  getDashboards(params?: GetDashboardsParams) {
    return http.get<DashboardsResponse>('/dashboards', {
      params,
    });
  },
  getDashboard({ dashboardId }: { dashboardId: number }) {
    return http.get<Dashboard>(`/dashboards/${dashboardId}`);
  },
  createDashboard({ data, params }: { data: DashboardRequest; params?: PostDashboardsParams }) {
    return http.post<Dashboard>(
      '/dashboards',
      {
        ...data,
      },
      {
        params,
      },
    );
  },
  updateDashboard({ dashboardId, data }: { dashboardId: number; data: Partial<DashboardRequest> }) {
    return http.patch<Dashboard>(`/dashboards/${dashboardId}`, {
      ...data,
    });
  },
  deleteDashboard({ dashboardId }: { dashboardId: number }) {
    return http.delete<void>(`/dashboards/${dashboardId}`);
  },
  // Reports
  getMetricTypes() {
    return http.get<MetricTypesResponse>('/metric_types');
  },
  getMetricDetails(params: GetMetricDetailsParams, config: HttpRequestConfig = {}) {
    return http.get<MetricDetailsResponse>('/metric_details', merge({ params }, config));
  },
  getComparableMetrics() {
    return http.get<ComparableMetricsResponse>(`/comparable_metrics`);
  },
  getReport({
    dashboardId,
    reportId,
    signal,
    params,
  }: {
    dashboardId: number;
    reportId: number;
    signal?: AbortSignal;
    params?: GetDashboardsDashboardIdReportsReportIdParams;
  }) {
    return http.get<ReportResponse>(`/dashboards/${dashboardId}/reports/${reportId}`, {
      params,
      signal,
    });
  },
  getGenericReportData(
    {
      brandIds,
      competitorSourceAccountIds,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      channels,
      metrics,
      reportType,
      timeScale,
      sortOrder,
      limit,
      aggregateBy,
      sourceAdAccountIds,
      sourceCampaignIds,
      sourceAdCampaigns,
      campaignIds,
      authorship,
      mediaTypes,
    }: {
      brandIds?: number[] | number;
      competitorSourceAccountIds?: string[];
      startDate?: string;
      endDate?: string;
      contextStartDate?: string;
      contextEndDate?: string;
      channels?: string[] | string;
      metrics?: string[] | string;
      reportType?: string;
      timeScale?: string;
      sortOrder?: string;
      limit?: number;
      aggregateBy?: string;
      sourceAdAccountIds?: string[];
      sourceCampaignIds?: string[];
      sourceAdCampaigns?: unknown[];
      campaignIds?: number[] | number;
      authorship?: readonly string[];
      mediaTypes?: readonly string[] | string;
    },
    config: HttpRequestConfig = {},
  ) {
    const signal = getAndSetSignal({
      brandIds,
      competitorSourceAccountIds,
      channels,
      metrics,
      reportType,
      aggregateBy,
      sourceAdAccountIds,
      sourceCampaignIds,
      campaignIds,
      authorship,
      mediaTypes,
    });

    // Note: response is typed but params/body stay loose. The OpenAPI spec
    // models /reports/data params with strict enum types and array shapes,
    // but this caller joins arrays into comma-separated strings and forwards
    // user-supplied `reportType`/`channels`/etc. as plain strings.
    return http.put<ReportDataResponse>(
      '/reports/data',
      {
        competitor_source_accountIds: competitorSourceAccountIds,
        source_ad_account_ids: sourceAdAccountIds,
        source_campaign_ids: sourceCampaignIds,
        source_ad_campaigns: sourceAdCampaigns,
        authorship,
      },
      merge(
        {
          params: {
            brandIds: Array.isArray(brandIds) ? brandIds.join(',') : brandIds,
            startDate,
            endDate,
            contextStartDate,
            contextEndDate,
            channels: Array.isArray(channels) ? channels.join(',') : channels,
            metrics: Array.isArray(metrics) ? metrics.join(',') : metrics,
            reportType,
            timeScale,
            sortOrder,
            limit,
            aggregateBy,
            campaignIds: Array.isArray(campaignIds) ? campaignIds.join(',') : campaignIds,
            mediaTypes: Array.isArray(mediaTypes) ? mediaTypes.join(',') : mediaTypes,
          },
          signal,
        },
        config,
      ),
    );
  },
  createReport({ dashboardId, data }: { dashboardId: number; data: ReportCreateRequest }) {
    return http.post<ReportResponse>(`/dashboards/${dashboardId}/reports`, {
      ...data,
    });
  },
  createReportsBatch({
    dashboardId,
    data,
  }: {
    dashboardId: number;
    data: {
      reports: { client_ref: string; type: string; meta: Record<string, unknown> }[];
      layout_placements: { client_ref: string; x: number; y: number; w: number; h: number }[];
    };
  }) {
    return http.post<ReportsBatchResponse>(`/dashboards/${dashboardId}/reports/batch`, data);
  },
  updateReport({
    dashboardId,
    reportId,
    data,
  }: {
    dashboardId: number;
    reportId: number;
    data: ReportUpdateRequest;
  }) {
    return http.patch<ReportResponse>(`/dashboards/${dashboardId}/reports/${reportId}`, {
      ...data,
    });
  },
  deleteReport({ dashboardId, reportId }: { dashboardId: number; reportId: number }) {
    return http.delete<void>(`/dashboards/${dashboardId}/reports/${reportId}`);
  },
  getTopLineStatsForChannel(
    {
      brandIds,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      channels,
      metrics,
      requirePosts,
      aggregateBy = 'BRAND',
      reportType = 'TOTAL_GROUPED_METRIC',
      authorship,
    }: {
      brandIds?: number[] | number;
      startDate?: string;
      endDate?: string;
      contextStartDate?: string;
      contextEndDate?: string;
      channels?: string[] | string;
      metrics?: string[] | string;
      requirePosts?: boolean;
      aggregateBy?: string;
      reportType?: string;
      authorship?: readonly string[];
    } = {},
    { signal }: { signal?: AbortSignal } = {},
  ) {
    const cancelOptions = signal
      ? { signal }
      : {
          signal: getAndSetSignal({
            brandIds,
            channels,
            metrics,
          }),
        };

    const params = {
      brandIds,
      startDate,
      endDate,
      contextStartDate,
      contextEndDate,
      requirePosts,
      metrics,
      channels,
      reportType,
      aggregateBy,
    };
    // `authorship` is a body field on the route, so a request carrying it is a PUT like
    // `getGenericReportData`; the route reads the same query params either way.
    if (authorship) {
      return http.put<ReportDataResponse>(
        `/reports/data`,
        { authorship },
        { params, ...cancelOptions },
      );
    }
    return http.get<ReportDataResponse>(`/reports/data`, { params, ...cancelOptions });
  },
  getGraphStats(
    {
      brandIds,
      campaignIds,
      startDate,
      endDate,
      metrics,
      channels,
      timeScale,
      aggregateBy,
      authorship,
    }: {
      brandIds?: number[] | number;
      campaignIds?: number[] | number;
      startDate?: string;
      endDate?: string;
      metrics?: string[] | string;
      channels?: string[] | string;
      timeScale?: string;
      aggregateBy?: string;
      authorship?: readonly string[];
    } = {},
    { signal }: { signal?: AbortSignal } = {},
  ) {
    const reportType = 'GRAPH';
    const cancelOptions = signal
      ? { signal }
      : {
          signal: getAndSetSignal({
            brandIds,
            campaignIds,
            channels,
            metrics,
            reportType,
            aggregateBy,
          }),
        };

    const params = {
      brandIds,
      campaignIds,
      startDate,
      endDate,
      metrics,
      channels,
      timeScale,
      reportType,
      aggregateBy,
    };
    // See `getTopLineStatsForChannel`: `authorship` travels in the body, so it makes this a PUT.
    if (authorship) {
      return http.put<ReportDataResponse>(
        `/reports/data`,
        { authorship },
        { params, ...cancelOptions },
      );
    }
    return http.get<ReportDataResponse>(`/reports/data`, { params, ...cancelOptions });
  },
  getBenchmarkGraphStats(
    {
      brandIds,
      startDate,
      endDate,
      metrics,
      channels,
      timeScale,
      benchmarks,
    }: {
      brandIds?: number[] | number;
      startDate?: string;
      endDate?: string;
      metrics?: string[] | string;
      channels?: string[] | string;
      timeScale?: string;
      benchmarks?: unknown;
    } = {},
    { signal }: { signal?: AbortSignal } = {},
  ) {
    const params = {
      brandIds,
      startDate,
      endDate,
      metrics,
      channels,
      timeScale,
      reportType: 'GRAPH',
      use_benchmark_cache: true,
    };
    return http.put<ReportDataResponse>(`/reports/data`, { benchmarks }, { params, signal });
  },
  // Exports
  getDashboardAutomatedExports({ dashboardId }: { dashboardId: number }) {
    return http.get<DashboardAutomatedExport[]>(`/dashboards/${dashboardId}/automated_exports`);
  },
  getAllDashboardAutomatedExports() {
    return http.get<UserDashboardsAutomatedExports[]>(`/dashboards/automated_exports`);
  },
  createDashboardAutomatedExports({
    dashboardId,
    autoExport,
  }: {
    dashboardId: number;
    autoExport: CreateDashboardAutomatedExportRequest;
  }) {
    return http.post<DashboardAutomatedExport>(
      `/dashboards/${dashboardId}/automated_exports`,
      autoExport,
    );
  },
  updateDashboardAutomatedExports({
    dashboardId,
    autoExportId,
    autoExport,
  }: {
    dashboardId: number | undefined;
    autoExportId: number | undefined;
    autoExport: UpdateDashboardAutomatedExportRequest;
  }) {
    return http.patch<DashboardAutomatedExport>(
      `/dashboards/${dashboardId}/automated_exports/${autoExportId}`,
      autoExport,
    );
  },
  deleteAutomatedExport({
    dashboardId,
    autoExportId,
  }: {
    dashboardId: number | undefined;
    autoExportId: number | undefined;
  }) {
    return http.delete<void>(`/dashboards/${dashboardId}/automated_exports/${autoExportId}`);
  },
  deleteDashboardAutomatedExportUser({
    dashboardId,
    autoExportId,
    userId,
  }: {
    dashboardId: number | undefined;
    autoExportId: number | undefined;
    userId: number;
  }) {
    return http.delete<void>(
      `/dashboards/${dashboardId}/automated_exports/${autoExportId}/recipients/${userId}`,
    );
  },
  // Params are hand-written as snake_case to bypass the decamelize interceptor
  // for `format`, `start_date`, etc. — kept as Record<string, unknown> since
  // the spec types (GetDashboardsDashboardIdReportsReportIdParams) are camelCase.
  async getReportCsv({
    dashboardId,
    reportId,
    startDate,
    endDate,
    contextStartDate,
    contextEndDate,
    timeScale,
    filterOverrides = {},
  }: {
    dashboardId: number;
    reportId: number;
    startDate?: string;
    endDate?: string;
    contextStartDate?: string;
    contextEndDate?: string;
    timeScale?: string | null;
    filterOverrides?: object;
  }) {
    const params = {
      start_date: startDate,
      end_date: endDate,
      context_start_date: contextStartDate,
      context_end_date: contextEndDate,
      time_scale: timeScale,
      format: 'csv',
      ...filterOverrides,
    };
    return http.get<Blob>(`/dashboards/${dashboardId}/reports/${reportId}`, {
      params,
      responseType: 'blob',
    });
  },
  // Same snake/camel impedance as getGenericReportData — params stay loose.
  getReportDataCsv({
    brandIds,
    startDate,
    endDate,
    contextStartDate,
    contextEndDate,
    channels,
    metrics,
    reportType,
    sortOrder,
    limit,
    timeScale,
    filenamePrefix,
    csvLayout,
  }: {
    brandIds?: number[] | number | string;
    startDate?: string;
    endDate?: string;
    contextStartDate?: string;
    contextEndDate?: string;
    channels?: string[] | string;
    metrics?: string[] | string;
    reportType?: string;
    sortOrder?: string;
    limit?: number;
    timeScale?: string;
    filenamePrefix?: string;
    csvLayout?: string;
  }) {
    return http.get<Blob>(
      `/reports/data`,
      merge(
        {
          params: {
            brandIds,
            startDate,
            endDate,
            contextStartDate,
            contextEndDate,
            channels,
            metrics,
            reportType,
            sortOrder,
            limit,
            timeScale,
            filenamePrefix,
            csvLayout,
          },
        },
        {
          params: { format: 'csv' },
          responseType: 'blob' as const,
        },
      ),
    );
  },
  downloadAdHocReportCsv({
    meta,
    reportType,
  }: {
    meta: Record<string, unknown>;
    reportType: string;
  }) {
    const serializeQueryValue = (value: unknown) => {
      if (Array.isArray(value)) return value.join(',');
      if (typeof value === 'object' && value !== null) return JSON.stringify(value);
      return value;
    };
    const params = Object.fromEntries(
      AD_HOC_REPORT_QUERY_KEYS.map((key) => [key, serializeQueryValue(meta[key])]),
    );
    params.brand_ids = serializeQueryValue(meta.brand_ids ?? meta.brand_id);
    params.brand_tags = serializeQueryValue(meta.brand_tags ?? meta.brand_tag_ids);
    params.channels = serializeQueryValue(
      meta.channels ??
        (meta.channel == null
          ? reportType === 'BENCHMARK_METRIC'
            ? ['INSTAGRAM_BENCHMARKS']
            : undefined
          : [meta.channel]),
    );
    params.context_start_date = serializeQueryValue(
      meta.context_start_date ?? meta.comparison_start_date ?? meta.start_date,
    );
    params.context_end_date = serializeQueryValue(
      meta.context_end_date ?? meta.comparison_end_date ?? meta.end_date,
    );
    params.report_type = reportType;
    params.format = 'csv';

    const body = Object.fromEntries(AD_HOC_REPORT_BODY_KEYS.map((key) => [key, meta[key]]));
    body.source_ad_campaigns = meta.source_ad_campaigns ?? meta.source_campaigns;
    body.community =
      meta.community ?? (meta.community_filters ? { filters: meta.community_filters } : undefined);
    return http.put<string>('/reports/data', body, { params });
  },
  downloadDashboardSpreadsheetById({ dashboardId }: { dashboardId: number }) {
    return http.get<Blob>(`/dashboards/${dashboardId}`, {
      params: { format: 'xlsx', direct_download: true },
      responseType: 'blob',
    });
  },
  // Overlaps with getDashboard via the `format` query param: when format is set,
  // the backend returns a binary spreadsheet rather than the JSON Dashboard
  // shape. Caller-side handling treats the response as opaque, so it's left
  // untyped here. Params are hand-snake-cased to bypass decamelize.
  getDashboardSpreadsheet({
    dashboardId,
    startDate,
    endDate,
    contextStartDate,
    contextEndDate,
    format,
    socketId,
    timeScale,
    filterOverrides = {},
  }: {
    dashboardId: number;
    startDate?: string;
    endDate?: string;
    contextStartDate?: string;
    contextEndDate?: string;
    format?: string;
    socketId?: string | null;
    timeScale?: string | null;
    filterOverrides?: object;
  }) {
    const params = {
      start_date: startDate,
      end_date: endDate,
      context_start_date: contextStartDate,
      context_end_date: contextEndDate,
      time_scale: timeScale,
      socket_id: socketId,
      format,
      ...filterOverrides,
    };
    return http.get(`/dashboards/${dashboardId}`, { params });
  },
  // Sharing
  getDashboardUsers({ dashboardId }: { dashboardId: number | undefined }) {
    return http.get<DashboardUserListItem[]>(`/dashboards/${dashboardId}/users`);
  },
  shareDashboard({
    dashboardId,
    userId,
    userRole,
  }: {
    dashboardId: number | undefined;
    userId: number | undefined;
    userRole: DashboardUserRequest['userRole'];
  }) {
    return http.put<DashboardUser>(`/dashboards/${dashboardId}/users/${userId}`, {
      user_role: userRole,
    });
  },
  updateDashboardOwner({
    dashboardId,
    userId,
  }: {
    dashboardId: number | undefined;
    userId: DashboardNewOwnershipRequest['userId'] | undefined;
  }) {
    return http.put<DashboardUser>(`/dashboards/${dashboardId}/owner`, {
      user_id: userId,
    });
  },
  deleteDashboardUser({
    dashboardId,
    userId,
  }: {
    dashboardId: number | undefined;
    userId: number | undefined;
  }) {
    return http.delete<void>(`/dashboards/${dashboardId}/users/${userId}`);
  },
  // Industries
  getIndustries(includeFollowerBuckets = true) {
    return http.get<Industry[]>(`/industries?include_follower_buckets=${includeFollowerBuckets}`);
  },
  async getIndustriesBySourceAccount(sourceAccountId: number) {
    return http.get<IndustryBySourceAccountResponse[]>(
      `/industries/source_account?source_account_id=${sourceAccountId}`,
    );
  },
  getHubspotIndustries() {
    return http.get<BenchmarkIndustry[]>('/benchmark/industries');
  },

  // Custom Metrics
  createCustomMetric({
    organizationId,
    params,
  }: {
    organizationId: number;
    params: CustomMetricCreateRequest;
  }) {
    return http.post<CustomMetricResponse>(`organizations/${organizationId}/custom_metric`, params);
  },

  getCustomMetricsUsage({ brandIds }: { brandIds: CustomMetricBrandAndOrgRequest['brandIds'] }) {
    return http.post<PostCustomMetricsUsage200>(`/custom_metrics/usage`, { brandIds });
  },

  getCustomMetrics({
    organizationIds,
    brandIds,
    params,
  }: {
    organizationIds?: CustomMetricBrandAndOrgRequest['organizationIds'];
    brandIds?: CustomMetricBrandAndOrgRequest['brandIds'];
    params?: PostCustomMetricsParams;
  }) {
    let payload: CustomMetricPostRequest | undefined;
    if (organizationIds) payload = { organizationIds } as CustomMetricPostRequest;
    else if (brandIds) payload = { brandIds } as CustomMetricPostRequest;
    return http.post<PagedCustomMetrics>('/custom_metrics', payload, {
      params,
    });
  },

  updateCustomMetric({
    organizationId,
    metricId,
    params,
  }: {
    organizationId: number;
    metricId: number;
    params: CustomMetricUpdateRequest;
  }) {
    return http.patch<CustomMetricResponse>(
      `organizations/${organizationId}/custom_metric/${metricId}`,
      params,
    );
  },
  deleteCustomMetric({ organizationId, metricId }: { organizationId: number; metricId: number }) {
    return http.delete<void>(`organizations/${organizationId}/custom_metric/${metricId}`);
  },
  // Shareable Dashboard Links
  getShareableDashboardLinks({ userId, dashboardId, brandId }: GetShareableLinksParams) {
    const params = {
      userId,
      dashboardId,
      brandId,
    };
    return http.get<ShareableDashboardLinkListResponse>(`/shareable_links`, {
      params,
      camelizeKeys: true,
    });
  },
  getShareableDashboardLink({ shareableLinkId }: { shareableLinkId: number }) {
    return http.get<ShareableDashboardLink>(`/shareable_links/${shareableLinkId}`, {
      camelizeKeys: true,
    });
  },
  createShareableDashboardLink(data: ShareableDashboardLinkCreateRequest) {
    // oxlint-disable-next-line typescript/no-unnecessary-type-arguments
    return http.post<ShareableDashboardLink>('/shareable_links', data, {
      camelizeKeys: true,
    });
  },
  updateShareableDashboardLink({
    shareableLinkId,
    ...data
  }: { shareableLinkId: number } & ShareableDashboardLinkUpdateRequest) {
    // oxlint-disable-next-line typescript/no-unnecessary-type-arguments
    return http.patch<ShareableDashboardLink>(`/shareable_links/${shareableLinkId}`, data, {
      camelizeKeys: true,
    });
  },
  deleteShareableDashboardLink({ shareableLinkId }: { shareableLinkId: number }) {
    return http.delete<void>(`/shareable_links/${shareableLinkId}`, { camelizeKeys: true });
  },
  // No `camelizeKeys`, unlike the CRUD calls above: the generated response type is keyed by
  // dashboard id with snake_case entry fields, so the payload is consumed as sent.
  // `dashboard_ids` is a webargs DelimitedList, so it has to go over the wire comma-joined —
  // axios' default array format (`dashboard_ids[]=`) would not parse.
  getShareableLinksUsage({ dashboardIds }: { dashboardIds: number[] }) {
    return http.get<GetShareableLinksUsage200>('/shareable_links/usage', {
      params: { dashboardIds: dashboardIds.join(',') },
    });
  },
};

export default api;
